/**
 * Grouping Metadata Modal - React Component
 *
 * Modal dialog for collecting metadata when grouping multiple elements into an atomic unit.
 * Rendered in parent React app (not iframe) for proper overlay positioning.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Layers } from 'lucide-react';

export interface GroupMetadata {
  groupId: string;
  role: 'content' | 'structure';
  editableUser: boolean;
  editableAi: boolean;
}

interface GroupingModalProps {
  isOpen: boolean;
  elementCount: number;
  onSubmit: (metadata: GroupMetadata) => void;
  onCancel: () => void;
}

export const GroupingModal: React.FC<GroupingModalProps> = ({
  isOpen,
  elementCount,
  onSubmit,
  onCancel,
}) => {
  const [groupId, setGroupId] = useState('');
  const [role, setRole] = useState<'content' | 'structure'>('content');
  const [editableUser, setEditableUser] = useState(true);
  const [editableAi, setEditableAi] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const metadata: GroupMetadata = {
      groupId: groupId.trim(),
      role,
      editableUser,
      editableAi,
    };

    onSubmit(metadata);

    // Reset form
    setGroupId('');
    setRole('content');
    setEditableUser(true);
    setEditableAi(true);
  };

  const handleCancel = () => {
    // Reset form
    setGroupId('');
    setRole('content');
    setEditableUser(true);
    setEditableAi(true);

    onCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Layers className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Create Atomic Group</DialogTitle>
              <DialogDescription>
                Grouping {elementCount} elements into a single atomic unit
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Group ID (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="groupId">
              Group ID <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="groupId"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              placeholder="e.g., topic-card-1, header-section"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Semantic identifier for this group (kebab-case recommended)
            </p>
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={role} onValueChange={(value: 'content' | 'structure') => setRole(value)}>
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="content">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Content</span>
                    <span className="text-xs text-muted-foreground">
                      Message-related elements (text, logos, images)
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="structure">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Structure</span>
                    <span className="text-xs text-muted-foreground">
                      Design/decoration (backgrounds, patterns, shapes)
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Editability */}
          <div className="space-y-3">
            <Label>Editability</Label>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="editableUser"
                checked={editableUser}
                onCheckedChange={(checked) => setEditableUser(checked as boolean)}
              />
              <label
                htmlFor="editableUser"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                User can edit
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="editableAi"
                checked={editableAi}
                onCheckedChange={(checked) => setEditableAi(checked as boolean)}
              />
              <label
                htmlFor="editableAi"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                AI can edit
              </label>
            </div>

            <p className="text-xs text-muted-foreground">
              Control who can modify this group and its children
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button type="submit" className="gap-2">
              <Layers className="h-4 w-4" />
              Create Group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
